import React from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  Button,
  Grid,
  TextField,
  Box,
  Typography,
  Chip,
  FormHelperText,
} from "@mui/material";
import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { useAuth } from "hooks/useAuth";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import {
  SMS_TEMPLATE_TYPES,
  processID,
  getTemplateOptions,
} from "constants/smsTemplateConstants";

const INITIAL_STATE = {
  account: "",
  entity: "",
  templateName: "",
  content: "",
  type: "Vendor",
  applicableFor: "",
  isActive: true,
};

const getProcessOptions = () => {
  return Object.entries(SMS_TEMPLATE_TYPES).map(([key, value]) => ({
    code: value.code,
    name: key
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase()),
    variables: value.variables,
    template: value.template,
  }));
};

const SmsTemplateForm = ({
  isBusy = false,
  smsTemplateDetail = null,
  onSubmit = () => {},
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  // RTK Query
  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();

  // React Hook Form setup
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account || "",
    },
  });
  const watchedAccount = useWatch({ control, name: "account" });
  const watchedApplicableFor = useWatch({ control, name: "applicableFor" });
  const watchedContent = useWatch({ control, name: "content" });

  // Process options
  const processOptions = React.useMemo(() => getProcessOptions(), []);
  const [selectedProcess, setSelectedProcess] = React.useState(null);

  // Filter process options based on processID
  const filteredProcessOptions = React.useMemo(() => {
    return processOptions.filter((option) => processID.includes(option.code));
  }, [processOptions]);

  // Update selected process when applicableFor changes
  React.useEffect(() => {
    if (watchedApplicableFor) {
      const selected = filteredProcessOptions.find(
        (p) => p.code === parseInt(watchedApplicableFor)
      );
      setSelectedProcess(selected);
    } else {
      setSelectedProcess(null);
    }
  }, [watchedApplicableFor, filteredProcessOptions]);

  // Fetch entities for admins
  React.useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: watchedAccount?._id || viewEntity?.entity?.account,
      });
    }
  }, [
    isAccountAdmin,
    isSystemAdmin,
    watchedAccount?._id,
    fetchEntities,
    viewEntity?.entity?.account,
  ]);

  // Populate form for editing
  React.useEffect(() => {
    if (!smsTemplateDetail) return;

    reset({
      account: smsTemplateDetail?.account ?? viewEntity?.entity?.account ?? "",
      entity: smsTemplateDetail?.entity ?? viewEntity?.entity ?? "",
      templateName: smsTemplateDetail?.templateName ?? "",
      content: smsTemplateDetail?.content ?? "",
      type: smsTemplateDetail?.type ?? "Vendor",
      applicableFor: smsTemplateDetail?.applicableFor?.toString() ?? "",
      isActive: smsTemplateDetail?.isActive ?? true,
    });
  }, [smsTemplateDetail, reset, viewEntity?.entity]);

  // Insert variable into content
  const insertVariable = (variable) => {
    const currentContent = watchedContent || "";
    const newContent = currentContent + `{${variable}}`;
    setValue("content", newContent);
  };

  // Use default template
  const useDefaultTemplate = () => {
    if (selectedProcess) {
      setValue("content", selectedProcess.template);
      setValue("templateName", `Template for ${selectedProcess.name}`);
    }
  };

  // Handle form submission
  const handleOnSmsTemplateSubmit = (data) => {
    const _smsTemplatePayload = {
      ...data,
      account: data?.account?._id,
      entity: data?.entity?._id,
      applicableFor: parseInt(data.applicableFor),
      variables: selectedProcess ? selectedProcess.variables : [],
    };
    onSubmit(_smsTemplatePayload);
  };

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={2}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="Account"
                    required
                    value={value ?? ""}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("entity", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}

          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? ""}
                    onChange={(_, data) => onChange(data)}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <Controller
              name="applicableFor"
              control={control}
              rules={{
                required: "Please select a process ID.",
                validate: (value) =>
                  processID.includes(parseInt(value)) || "Invalid process ID",
              }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Applicable For"
                  options={getTemplateOptions()}
                  getOptionLabel={(opt) => opt.label || ""}
                  getOptionValue={(opt) => opt.value || ""}
                  error={!!errors.applicableFor}
                  helperText={errors?.applicableFor?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="templateName"
              control={control}
              rules={{ required: "Please provide a template name." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Template Name"
                  error={!!errors.templateName}
                  helperText={errors?.templateName?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="type"
              control={control}
              rules={{ required: "Please select a type." }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Type"
                  options={[
                    { label: "Vendor", value: "Vendor" },
                    { label: "System", value: "System" },
                  ]}
                  getOptionLabel={(opt) => opt.label || ""}
                  getOptionValue={(opt) => opt.value || ""}
                  error={!!errors.type}
                  helperText={errors?.type?.message}
                />
              )}
            />
          </Grid>

          {selectedProcess && (
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Available Variables:
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                  {selectedProcess.variables.map((variable) => (
                    <Chip
                      key={variable}
                      label={`{${variable}}`}
                      onClick={() => insertVariable(variable)}
                      clickable
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={useDefaultTemplate}
                  sx={{ mb: 2 }}
                >
                  Use Default Template
                </Button>
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Controller
              name="content"
              control={control}
              rules={{
                required: "Please provide template content.",
                maxLength: {
                  value: 160,
                  message: "Content cannot exceed 160 characters.",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Content"
                  multiline
                  rows={3}
                  error={!!errors.content}
                />
              )}
            />
            <Box
              sx={{
                mt: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <FormHelperText>
                {!!errors?.content ? (
                  <span style={{ color: "red" }}>{errors.content.message}</span>
                ) : (
                  "Click variable chips above to insert them into content."
                )}
              </FormHelperText>
              <Typography
                variant="caption"
                color={watchedContent?.length > 160 ? "error" : "textSecondary"}
              >
                {watchedContent?.length || 0}/160 characters
              </Typography>
            </Box>
          </Grid>

          {selectedProcess && (
            <Grid item xs={12}>
              <Box sx={{ p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Default Template Preview:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {selectedProcess.template}
                </Typography>
              </Box>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <AppSwitch {...field} label="Is Active?" />
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnSmsTemplateSubmit)}
          disabled={isBusy}
        >
          {isBusy ? "Saving..." : smsTemplateDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default SmsTemplateForm;
